import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, UserPlus } from 'lucide-react';
import FilterDropdown from '../../components/common/FilterDropdown';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import CreateRoleForm from './components/CreateRoleForm';
import ViewRole from './components/ViewRole';
import CreateUserForm from './components/CreateUserForm';
import { getRoles, deleteRole, updateRole, createRole } from './api/roleApi';
import { getUsers, deleteUser, updateUser } from './api/userApi';
import { getCompetencyRequestsByUser } from './api/competencyApi';
import { getUserDocuments, downloadDocument } from './api/documentApi';

const CreateRole = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [userListPage, setUserListPage] = useState(0);
  const [userListSize] = useState(10);
  const [userDetailTab, setUserDetailTab] = useState('profile'); // 'profile' or 'security'
  const [accountForm, setAccountForm] = useState({ username: '', email: '', nohp: '' });
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [passwordResetMessage, setPasswordResetMessage] = useState('');
  const [typeUnits, setTypeUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('roles'); // 'roles' or 'users'
  const [competencyRows, setCompetencyRows] = useState([]);
  const [competencyLoading, setCompetencyLoading] = useState(false);
  const [competencySearch, setCompetencySearch] = useState('');
  const [competencyStandardFilter, setCompetencyStandardFilter] = useState('');
  const [competencyStatusFilter, setCompetencyStatusFilter] = useState('');
  const [documentRows, setDocumentRows] = useState([]);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentPage, setDocumentPage] = useState(0);
  const [documentSize] = useState(10);

  const getUserInitials = (u) => {
    if (!u) return '';
    const username = u.username || u.email || '';
    return username.slice(0, 2).toUpperCase();
  };

  const getInitialsColor = (initials) => {
    const colors = {
      'RJ': '#6366f1', 'JD': '#f97316', 'CC': '#06b6d4', 'EE': '#8b5cf6', 'MT': '#ec4899',
      'AP': '#14b8a6', 'BN': '#f43f5e', 'DS': '#6366f1', 'FT': '#f97316', 'GL': '#06b6d4',
      'HS': '#8b5cf6', 'IR': '#ec4899', 'JW': '#14b8a6', 'KL': '#f43f5e', 'ZM': '#6366f1'
    };
    return colors[initials] || '#6366f1';
  };

  const getUserStatus = (u) => {
    const status = String(u?.status || '').toLowerCase();
    if (status === '1' || status === 'active') return 'active';
    return status || 'inactive';
  };

  const competencyStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'approved') return 'text-success';
    if (s === 'rejected') return 'text-danger';
    if (s === 'revise' || s === 'revision') return 'text-warning';
    return 'text-secondary';
  };

  const formatRoleList = (roles) => {
    if (!roles || !Array.isArray(roles)) return [];
    return roles.map(role => role.name || role);
  };

  useEffect(() => {
    loadRoles();
    loadUsers();
  }, []);

  const loadRoles = async () => {
    setIsLoading(true);
    try {
      const response = await getRoles();
      console.log('API Response:', response);
      
      let data = (Array.isArray(response) ? response : []).map(item => ({
        ...item,
        nameRole: item.name || item.nameRole,
        description: item.description || 'No description available',
        createDate: item.createDate || item.createdAt || item.cdate || '',
      }));
      
      console.log('Processed data:', data);
      setRoles(data);
    } catch (error) {
      console.error('Error loading roles:', error);
      setRoles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    setIsUserLoading(true);
    try {
      const response = await getUsers();
      console.log('Users API Response:', response);
      
      let data = (Array.isArray(response) ? response : []).map(item => ({
        ...item,
        id: item.id,
        username: item.username || '',
        email: item.email || '',
        nohp: item.nohp || '',
        status: item.status || '',
        roles: item.roles || [],
        createDate: item.createDate || item.createdAt || item.cdate || '',
      }));
      
      console.log('Processed users data:', data);
      setUsers(data);
      setSelectedUser(prev => {
        if (data.length === 0) return null;
        if (!prev) return data[0];
        return data.find(u => String(u.id) === String(prev.id)) || data[0];
      });
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setIsUserLoading(false);
    }
  };

  // Filter roles based on search query
  const filteredRoles = roles.filter((role) => {
    const query = searchQuery.toLowerCase();
    return (
      role.nameRole?.toLowerCase().includes(query) ||
      role.description?.toLowerCase().includes(query) ||
      role.createDate?.toLowerCase().includes(query)
    );
  });

  // Filter users based on search query
  const filteredUsers = users.filter((user) => {
    const query = userSearchQuery.toLowerCase();
    return (
      user.username?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.nohp?.toLowerCase().includes(query) ||
      user.status?.toLowerCase().includes(query)
    );
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aActive = getUserStatus(a) === 'active' ? 0 : 1;
    const bActive = getUserStatus(b) === 'active' ? 0 : 1;
    return aActive - bActive;
  });

  const userListTotalPages = Math.ceil(sortedUsers.length / userListSize) || 0;
  const pagedUsers = sortedUsers.slice(userListPage * userListSize, (userListPage + 1) * userListSize);

  useEffect(() => {
    setUserListPage(0);
  }, [userSearchQuery]);

  useEffect(() => {
    setUserDetailTab('profile');
    setPasswordResetMessage('');
    setAccountForm({
      username: selectedUser?.username || '',
      email: selectedUser?.email || '',
      nohp: selectedUser?.nohp || ''
    });
    setCompetencyRows([]);
    setCompetencySearch('');
    setCompetencyStandardFilter('');
    setCompetencyStatusFilter('');
    setDocumentRows([]);
    setDocumentSearch('');
    setDocumentPage(0);
  }, [selectedUser?.id]);

  useEffect(() => {
    setDocumentPage(0);
  }, [documentSearch]);

  useEffect(() => {
    if (userDetailTab !== 'document' || !selectedUser?.id) return;
    let cancelled = false;
    setDocumentLoading(true);
    getUserDocuments(selectedUser.id)
      .then((data) => {
        if (!cancelled) setDocumentRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setDocumentRows([]);
      })
      .finally(() => {
        if (!cancelled) setDocumentLoading(false);
      });
    return () => { cancelled = true; };
  }, [userDetailTab, selectedUser?.id]);

  const filteredDocumentRows = documentRows.filter((doc) => {
    const query = documentSearch.toLowerCase();
    if (!query) return true;
    return [doc.documentName, doc.fileName, doc.category, doc.sourceName, doc.uploadBy]
      .some((v) => String(v || '').toLowerCase().includes(query));
  });

  const documentTotalPages = Math.ceil(filteredDocumentRows.length / documentSize) || 0;
  const pagedDocumentRows = filteredDocumentRows.slice(documentPage * documentSize, (documentPage + 1) * documentSize);

  useEffect(() => {
    if (documentPage > 0 && documentPage >= documentTotalPages) {
      setDocumentPage(Math.max(0, documentTotalPages - 1));
    }
  }, [documentTotalPages, documentPage]);

  const formatFileSize = (bytes) => {
    if (bytes === null || bytes === undefined) return '-';
    const size = Number(bytes);
    if (Number.isNaN(size)) return '-';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  useEffect(() => {
    if (userDetailTab !== 'competency' || !selectedUser?.id) return;
    let cancelled = false;
    setCompetencyLoading(true);
    getCompetencyRequestsByUser(selectedUser.id)
      .then((data) => {
        if (!cancelled) setCompetencyRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setCompetencyRows([]);
      })
      .finally(() => {
        if (!cancelled) setCompetencyLoading(false);
      });
    return () => { cancelled = true; };
  }, [userDetailTab, selectedUser?.id]);

  const filteredCompetencyRows = competencyRows.filter((row) => {
    const query = competencySearch.toLowerCase();
    const matchesQuery = !query || [row.qualificationName, row.standard, row.sectors]
      .some((v) => String(v || '').toLowerCase().includes(query));
    const matchesStandard = !competencyStandardFilter || row.standard === competencyStandardFilter;
    const matchesStatus = !competencyStatusFilter || String(row.status || '').toLowerCase() === competencyStatusFilter.toLowerCase();
    return matchesQuery && matchesStandard && matchesStatus;
  });

  const competencyStandardOptions = Array.from(new Set(competencyRows.map((r) => r.standard).filter(Boolean)));

  useEffect(() => {
    if (userListPage > 0 && userListPage >= userListTotalPages) {
      setUserListPage(Math.max(0, userListTotalPages - 1));
    }
  }, [userListTotalPages, userListPage]);

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterReset = () => {
    setFilterData({
      dateFrom: '',
      dateTo: '',
      client: '',
      project: '',
      status: ''
    });
  };

  const handleFilterApply = () => {
    // Add your filter logic here
  };

  const roleColumns = [
    { key: 'nameRole', label: 'Name Role' },
    { key: 'description', label: 'Description' },
    { key: 'createDate', label: 'Create Date' },
  ];

  const handleFormSubmit = async (formData) => {
    try {
      if (selectedWorkflow) {
        await updateRole(selectedWorkflow.id, formData);
      } else {
        await createRole(formData);
      }
      await loadRoles();
      setIsModalOpen(false);
      setSelectedWorkflow(null); // Reset selected workflow after successful save
    } catch (error) {
      console.error('Error saving role:', error);
      // Re-throw error so the form component can handle it
      throw error;
    }
  };

  const handleUserFormSubmit = async () => {
    // CreateUserForm already performs the create/update (and photo/signature upload)
    // API calls itself and closes its own modal — this just refreshes the list.
    try {
      await loadUsers();
    } catch (error) {
      console.error('Error refreshing users:', error);
    }
    setIsUserModalOpen(false);
    setEditingUser(null);
  };

  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setAccountForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveAccount = async () => {
    if (!selectedUser) return;
    setIsSavingAccount(true);
    try {
      await updateUser(selectedUser.id, {
        username: accountForm.username,
        email: accountForm.email,
        nohp: accountForm.nohp,
        password: '',
        status: selectedUser.status || '',
        roles: formatRoleList(selectedUser.roles)
      });
      setPasswordResetMessage('Account updated successfully.');
      await loadUsers();
    } catch (error) {
      console.error('Error updating account:', error);
      setPasswordResetMessage('Failed to update account.');
    } finally {
      setIsSavingAccount(false);
      setTimeout(() => setPasswordResetMessage(''), 3000);
    }
  };

  const handleDocumentDownload = async (doc) => {
    if (!doc.downloadUrl) return;
    try {
      await downloadDocument(doc.downloadUrl, doc.fileName);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const handleResetPassword = () => {
    if (!selectedUser) return;
    handleUserEdit(selectedUser);
  };

  // Role handlers
  const handleEdit = (row) => {
    setSelectedWorkflow(row);
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        await deleteRole(row.id);
        await loadRoles();
      } catch (error) {
        console.error('Error deleting role:', error);
      }
    }
  };

  const handleView = (row) => {
    setSelectedWorkflow(row);
    setIsViewModalOpen(true);
  };

  // User handlers
  const handleUserEdit = (row) => {
    setEditingUser(row);
    setIsUserModalOpen(true);
  };

  const openCreateUserModal = () => {
    setEditingUser(null);
    setIsUserModalOpen(true);
  };

  const handleUserDelete = async (row) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(row.id);
        if (selectedUser && String(selectedUser.id) === String(row.id)) {
          setSelectedUser(null);
        }
        await loadUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  return (
    <div className="container-xl">
      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Role & User Management</h3>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list">
            {activeTab === 'roles' && (
              <button
                className="btn btn-primary"
                onClick={() => setIsModalOpen(true)}
              >
                <Plus size={18} className="me-1" />
                Create Role
              </button>
            )}
            {activeTab === 'users' && (
              <button
                className="btn btn-primary"
                onClick={openCreateUserModal}
              >
                <UserPlus size={18} className="me-1" />
                Create User
              </button>
            )}
          </div>
        </div>
      </div>

      <CreateRoleForm
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedWorkflow(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedWorkflow}
      />

      <ViewRole
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedWorkflow(null);
        }}
        data={selectedWorkflow}
      />

      {/* Add the CreateUserForm modal */}
      <CreateUserForm
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false);
          setEditingUser(null);
        }}
        onSubmit={handleUserFormSubmit}
        initialData={editingUser}
        isEditMode={!!editingUser}
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <button
                      className={`nav-link${activeTab === 'roles' ? ' active' : ''}`}
                      onClick={() => setActiveTab('roles')}
                      type="button"
                      role="tab"
                    >
                      List Data
                    </button>
                  </li>
                  <li className="nav-item-tab" role="presentation">
                    <button
                      className={`nav-link${activeTab === 'users' ? ' active' : ''}`}
                      onClick={() => setActiveTab('users')}
                      type="button"
                      role="tab"
                    >
                      User
                    </button>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className={`tab-pane${activeTab === 'roles' ? ' active show' : ''}`} id="tabs-list-data" role="tabpanel">
                    {activeTab === 'roles' && (
                      <GenericView
                        data={filteredRoles}
                        columns={roleColumns}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onView={handleView}
                        isLoading={isLoading}
                        filterComponent={
                          <FilterDropdown
                            filterData={filterData}
                            onFilterChange={handleFilterChange}
                            onReset={handleFilterReset}
                            onApply={handleFilterApply}
                          />
                        }
                        searchComponent={
                          <SearchInput
                            value={searchQuery}
                            onChange={setSearchQuery}
                            placeholder="Search roles..."
                          />
                        }
                        actionButtons={
                          <ActionButtons
                            onExportExcel={() => console.log('Export Excel')}
                            onExportPDF={() => console.log('Export PDF')}
                          />
                        }
                      />
                    )}
                  </div>
                  <div className={`tab-pane${activeTab === 'users' ? ' active show' : ''}`} id="tabs-user-data" role="tabpanel">
                    {activeTab === 'users' && (
                      <div className="row">
                        {/* Left Sidebar - User List */}
                        <div className="col-md-4">
                          <div className="card">
                            <div className="card-header">
                              <div className="d-flex gap-2">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search ..."
                                  value={userSearchQuery}
                                  onChange={(e) => setUserSearchQuery(e.target.value)}
                                />
                                <button className="btn btn-primary" onClick={openCreateUserModal}>Create</button>
                              </div>
                            </div>
                            <div className="list-group list-group-flush">
                              {isUserLoading ? (
                                <div className="text-center py-4 text-muted">Loading users...</div>
                              ) : filteredUsers.length === 0 ? (
                                <div className="text-center py-4 text-muted">No users found</div>
                              ) : pagedUsers.map(u => (
                                <button
                                  key={u.id}
                                  className={`list-group-item list-group-item-action text-start py-3 ${selectedUser && selectedUser.id === u.id ? 'active' : ''}`}
                                  onClick={() => setSelectedUser(u)}
                                  style={{ border: 'none', borderBottom: '1px solid #e5e7eb' }}
                                >
                                  <div className="d-flex align-items-center gap-3">
                                    <div
                                      style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        background: getInitialsColor(getUserInitials(u)),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: 14
                                      }}
                                    >
                                      {getUserInitials(u)}
                                    </div>
                                    <div className="flex-grow-1">
                                      <div className="fw-500">{u.username}</div>
                                      <div className="small text-muted">{u.email || ''}</div>
                                    </div>
                                    {getUserStatus(u) === 'active' && (
                                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }}></div>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                            {userListTotalPages > 1 && (
                              <div className="card-footer d-flex align-items-center justify-content-center py-2">
                                <ul className="pagination pagination-sm m-0">
                                  <li className={`page-item ${userListPage === 0 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setUserListPage(p => Math.max(0, p - 1))} disabled={userListPage === 0}>←</button>
                                  </li>
                                  {Array.from({ length: Math.min(userListTotalPages, 5) }, (_, i) => {
                                    const pageNum = Math.max(0, userListPage - 2) + i;
                                    if (pageNum >= userListTotalPages) return null;
                                    return (
                                      <li key={pageNum} className={`page-item ${pageNum === userListPage ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => setUserListPage(pageNum)}>{pageNum + 1}</button>
                                      </li>
                                    );
                                  })}
                                  <li className={`page-item ${userListPage >= userListTotalPages - 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setUserListPage(p => Math.min(userListTotalPages - 1, p + 1))} disabled={userListPage >= userListTotalPages - 1}>→</button>
                                  </li>
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right Panel - User Details */}
                        <div className="col-md-8">
                          <div className="card">
                            <div className="card-header d-flex align-items-center justify-content-between">
                              {selectedUser ? (
                                <>
                                  <div className="d-flex align-items-center gap-3">
                                    <div
                                      style={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: '50%',
                                        background: getInitialsColor(getUserInitials(selectedUser)),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: 20
                                      }}
                                    >
                                      {getUserInitials(selectedUser)}
                                    </div>
                                    <div>
                                      <h5 className="mb-1">{selectedUser.username}</h5>
                                      <div className="small text-muted">{selectedUser.email || ''}</div>
                                    </div>
                                  </div>
                                  <span
                                    className={`badge badge-outline ${getUserStatus(selectedUser) === 'active' ? 'text-success' : 'text-danger'}`}
                                    style={{ height: 'fit-content' }}
                                  >
                                    {getUserStatus(selectedUser) === 'active' ? '● Active' : '● Inactive'}
                                  </span>
                                </>
                              ) : (
                                <div className="text-muted">No user selected</div>
                              )}
                            </div>

                            {selectedUser && (
                              <div className="card-header border-bottom-0">
                                <ul className="nav nav-tabs card-header-tabs" role="tablist">
                                  <li className="nav-item-tab" role="presentation">
                                    <a
                                      href="#tabs-user-profile"
                                      className={`nav-link ${userDetailTab === 'profile' ? 'active' : ''}`}
                                      role="tab"
                                      onClick={(e) => { e.preventDefault(); setUserDetailTab('profile'); }}
                                    >
                                      Info Profile
                                    </a>
                                  </li>
                                  <li className="nav-item-tab" role="presentation">
                                    <a
                                      href="#tabs-user-document"
                                      className={`nav-link ${userDetailTab === 'document' ? 'active' : ''}`}
                                      role="tab"
                                      onClick={(e) => { e.preventDefault(); setUserDetailTab('document'); }}
                                    >
                                      Document
                                    </a>
                                  </li>
                                  <li className="nav-item-tab" role="presentation">
                                    <a
                                      href="#tabs-user-activity"
                                      className={`nav-link ${userDetailTab === 'activity' ? 'active' : ''}`}
                                      role="tab"
                                      onClick={(e) => { e.preventDefault(); setUserDetailTab('activity'); }}
                                    >
                                      Activity Log
                                    </a>
                                  </li>
                                  <li className="nav-item-tab" role="presentation">
                                    <a
                                      href="#tabs-user-competency"
                                      className={`nav-link ${userDetailTab === 'competency' ? 'active' : ''}`}
                                      role="tab"
                                      onClick={(e) => { e.preventDefault(); setUserDetailTab('competency'); }}
                                    >
                                      Competency
                                    </a>
                                  </li>
                                  <li className="nav-item-tab" role="presentation">
                                    <a
                                      href="#tabs-user-performance"
                                      className={`nav-link ${userDetailTab === 'performance' ? 'active' : ''}`}
                                      role="tab"
                                      onClick={(e) => { e.preventDefault(); setUserDetailTab('performance'); }}
                                    >
                                      Performance
                                    </a>
                                  </li>
                                  <li className="nav-item-tab" role="presentation">
                                    <a
                                      href="#tabs-user-security"
                                      className={`nav-link ${userDetailTab === 'security' ? 'active' : ''}`}
                                      role="tab"
                                      onClick={(e) => { e.preventDefault(); setUserDetailTab('security'); }}
                                    >
                                      Security
                                    </a>
                                  </li>
                                </ul>
                              </div>
                            )}

                            <div className="card-body">
                              {!selectedUser ? (
                                <div className="text-center py-5 text-muted">Select a user to view details</div>
                              ) : userDetailTab === 'profile' ? (
                                <div>
                                  <div className="d-flex justify-content-end mb-3">
                                    <a
                                      href="#"
                                      title="Edit"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleUserEdit(selectedUser);
                                      }}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="icon icon-tabler icons-tabler-outline icon-tabler-edit"
                                      >
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                        <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                                        <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                                        <path d="M16 5l3 3" />
                                      </svg>
                                    </a>
                                  </div>
                                  <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                      <div className="text-muted small mb-1">Username</div>
                                      <div className="fw-500">{selectedUser.username}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="text-muted small mb-1">Employee ID</div>
                                      <div className="fw-500">{selectedUser.employeeId || '-'}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="text-muted small mb-1">Email</div>
                                      <div className="fw-500">{selectedUser.email || ''}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="text-muted small mb-1">Department</div>
                                      <div className="fw-500">{selectedUser.department || '-'}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="text-muted small mb-1">Phone</div>
                                      <div className="fw-500">{selectedUser.nohp || ''}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="text-muted small mb-1">Job Title</div>
                                      <div className="fw-500">{selectedUser.jobTitle || '-'}</div>
                                    </div>
                                    <div className="col-12">
                                      <div className="text-muted small mb-1">Address</div>
                                      <div className="fw-500">{selectedUser.address || '-'}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="text-muted small mb-1">Emergency Contact</div>
                                      <div className="fw-500">{selectedUser.emergencyContact || '-'}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="text-muted small mb-1">Emergency Address</div>
                                      <div className="fw-500">{selectedUser.emergencyAddress || '-'}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="text-muted small mb-1">Status</div>
                                      <div className="fw-500">{getUserStatus(selectedUser) === 'active' ? 'Active' : 'Inactive'}</div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="text-muted small mb-1">Join Date</div>
                                      <div className="fw-500">{selectedUser.createDate || '-'}</div>
                                    </div>
                                  </div>

                                  <hr />
                                  <div className="mt-4">
                                    <h6 className="mb-3">User Roles</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                      {formatRoleList(selectedUser.roles).length === 0 ? (
                                        <span className="text-muted small">No roles assigned</span>
                                      ) : formatRoleList(selectedUser.roles).map((role, idx) => (
                                        <span key={idx} className="badge badge-outline text-secondary">
                                          {role}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ) : userDetailTab === 'document' ? (
                                <div>
                                  <div className="mb-3 d-flex gap-2 align-items-center justify-content-between">
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Search ..."
                                      style={{ maxWidth: '400px' }}
                                      value={documentSearch}
                                      onChange={(e) => setDocumentSearch(e.target.value)}
                                    />
                                    <span className="text-muted small">
                                      Documents from Proposals, Projects, and Leads created by this user
                                    </span>
                                  </div>
                                  <div className="table-responsive">
                                    <table className="table table-vcenter table-hover">
                                      <thead>
                                        <tr>
                                          <th>CATEGORY</th>
                                          <th>DOCUMENT NAME</th>
                                          <th>SOURCE</th>
                                          <th>SIZE</th>
                                          <th>UPLOAD DATE</th>
                                          <th>UPLOAD BY</th>
                                          <th>STATUS</th>
                                          <th>ACTION</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {documentLoading ? (
                                          <tr>
                                            <td colSpan="8" className="text-center py-4 text-muted">Loading documents...</td>
                                          </tr>
                                        ) : filteredDocumentRows.length === 0 ? (
                                          <tr>
                                            <td colSpan="8" className="text-center py-4 text-muted">
                                              No documents found
                                            </td>
                                          </tr>
                                        ) : pagedDocumentRows.map((doc) => (
                                          <tr key={doc.id}>
                                            <td>{doc.category || '-'}</td>
                                            <td>{doc.documentName}</td>
                                            <td>
                                              <span className="badge badge-outline text-secondary me-1">{doc.source}</span>
                                              <span className="small text-muted">{doc.sourceName}</span>
                                            </td>
                                            <td>{formatFileSize(doc.fileSize)}</td>
                                            <td>{doc.uploadDate ? new Date(doc.uploadDate).toLocaleDateString() : '-'}</td>
                                            <td>{doc.uploadBy}</td>
                                            <td>
                                              <span className="badge badge-outline text-success">{doc.status}</span>
                                            </td>
                                            <td>
                                              <a
                                                href="#"
                                                title="Download"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  if (doc.downloadUrl) handleDocumentDownload(doc);
                                                }}
                                                style={{
                                                  opacity: doc.downloadUrl ? 1 : 0.4,
                                                  cursor: doc.downloadUrl ? 'pointer' : 'not-allowed',
                                                  pointerEvents: doc.downloadUrl ? 'auto' : 'none',
                                                }}
                                              >
                                                <svg
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  width="24"
                                                  height="24"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  strokeWidth="2"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  className="icon icon-tabler icons-tabler-outline icon-tabler-download"
                                                >
                                                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                  <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
                                                  <path d="M7 11l5 5l5 -5" />
                                                  <path d="M12 4l0 12" />
                                                </svg>
                                              </a>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  {documentTotalPages > 1 && (
                                    <div className="d-flex align-items-center justify-content-center py-2">
                                      <ul className="pagination pagination-sm m-0">
                                        <li className={`page-item ${documentPage === 0 ? 'disabled' : ''}`}>
                                          <button className="page-link" onClick={() => setDocumentPage(p => Math.max(0, p - 1))} disabled={documentPage === 0}>&larr;</button>
                                        </li>
                                        {Array.from({ length: Math.min(documentTotalPages, 5) }, (_, i) => {
                                          const pageNum = Math.max(0, documentPage - 2) + i;
                                          if (pageNum >= documentTotalPages) return null;
                                          return (
                                            <li key={pageNum} className={`page-item ${pageNum === documentPage ? 'active' : ''}`}>
                                              <button className="page-link" onClick={() => setDocumentPage(pageNum)}>{pageNum + 1}</button>
                                            </li>
                                          );
                                        })}
                                        <li className={`page-item ${documentPage >= documentTotalPages - 1 ? 'disabled' : ''}`}>
                                          <button className="page-link" onClick={() => setDocumentPage(p => Math.min(documentTotalPages - 1, p + 1))} disabled={documentPage >= documentTotalPages - 1}>&rarr;</button>
                                        </li>
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              ) : userDetailTab === 'activity' ? (
                                <div>
                                  <div className="mb-3 d-flex gap-2 align-items-center justify-content-between">
                                    <input type="text" className="form-control" placeholder="Search ..." disabled style={{ maxWidth: '400px' }} />
                                    <button className="btn btn-outline-primary" disabled>Export</button>
                                  </div>
                                  <div className="table-responsive">
                                    <table className="table table-hover table-sm mb-0">
                                      <thead className="table-light">
                                        <tr>
                                          <th>Date/Time</th>
                                          <th>User</th>
                                          <th>Action</th>
                                          <th>Status Change</th>
                                          <th>Details</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td colSpan="5" className="text-center py-4 text-muted">
                                            Activity log is not available for CRM users
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : userDetailTab === 'competency' ? (
                                <div>
                                  <div className="mb-3 d-flex gap-2 align-items-end">
                                    <input
                                      className="form-control"
                                      placeholder="Search by name, standard..."
                                      value={competencySearch}
                                      onChange={(e) => setCompetencySearch(e.target.value)}
                                    />
                                    <select
                                      className="form-select"
                                      style={{ width: '200px' }}
                                      value={competencyStandardFilter}
                                      onChange={(e) => setCompetencyStandardFilter(e.target.value)}
                                    >
                                      <option value="">All Standards</option>
                                      {competencyStandardOptions.map((std) => (
                                        <option key={std} value={std}>{std}</option>
                                      ))}
                                    </select>
                                    <select
                                      className="form-select"
                                      style={{ width: '200px' }}
                                      value={competencyStatusFilter}
                                      onChange={(e) => setCompetencyStatusFilter(e.target.value)}
                                    >
                                      <option value="">All Status</option>
                                      <option value="Pending">Pending</option>
                                      <option value="Approved">Approved</option>
                                      <option value="Rejected">Rejected</option>
                                      <option value="Revise">Revise</option>
                                    </select>
                                  </div>
                                  <div className="table-responsive">
                                    <table className="table table-vcenter card-table table-hover">
                                      <thead>
                                        <tr>
                                          <th>Type</th>
                                          <th>Standard</th>
                                          <th>Qualification</th>
                                          <th>Sector/Scheme</th>
                                          <th>Request Date</th>
                                          <th>Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {competencyLoading ? (
                                          <tr>
                                            <td colSpan="6" className="text-center py-4 text-muted">Loading...</td>
                                          </tr>
                                        ) : filteredCompetencyRows.length === 0 ? (
                                          <tr>
                                            <td colSpan="6" className="text-center py-4 text-muted">
                                              No competency requests found
                                            </td>
                                          </tr>
                                        ) : filteredCompetencyRows.map((row) => (
                                          <tr key={row.id}>
                                            <td>
                                              <span className="badge badge-outline text-secondary">{row.requestType || '-'}</span>
                                            </td>
                                            <td>{row.standard || '-'}</td>
                                            <td>
                                              {row.requestType === 'Upgrade' && row.currentTypeName ? (
                                                <span className="badge badge-outline text-secondary">
                                                  {row.currentTypeName} &rarr; {row.requestedTypeName || row.qualificationName}
                                                </span>
                                              ) : (
                                                <span className="badge badge-outline text-secondary">{row.qualificationName || '-'}</span>
                                              )}
                                            </td>
                                            <td>{row.sectors || '-'}</td>
                                            <td>{row.requestDate ? new Date(row.requestDate).toLocaleDateString() : '-'}</td>
                                            <td>
                                              <span className={`badge badge-outline ${competencyStatusBadge(row.status)}`}>{row.status || 'Pending'}</span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : userDetailTab === 'performance' ? (
                                <div>
                                  <div className="mb-3">
                                    <select className="form-select" disabled>
                                      <option>Select performance</option>
                                    </select>
                                  </div>
                                  <div className="text-center py-4 text-muted">
                                    Performance metrics are not available for CRM users
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div className="row g-3">
                                    <div className="col-md-6">
                                      <div className="card">
                                        <div className="card-body">
                                          <h6 className="mb-2">Reset Password</h6>
                                          <p className="text-muted small">Open the edit form to set a new password for this user.</p>
                                          <div className="d-flex gap-2">
                                            <button className="btn btn-danger" onClick={handleResetPassword}>Reset Password</button>
                                            <button className="btn btn-outline-secondary" onClick={() => setPasswordResetMessage('')}>Clear</button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="col-md-6">
                                      <div className="card">
                                        <div className="card-body">
                                          <h6 className="mb-2">Account Data</h6>
                                          <div className="mb-2">
                                            <label className="form-label small">Username</label>
                                            <input name="username" value={accountForm.username} onChange={handleAccountChange} className="form-control" />
                                          </div>
                                          <div className="mb-2">
                                            <label className="form-label small">Email</label>
                                            <input name="email" value={accountForm.email} onChange={handleAccountChange} className="form-control" />
                                          </div>
                                          <div className="mb-2">
                                            <label className="form-label small">Phone Number</label>
                                            <input name="nohp" value={accountForm.nohp} onChange={handleAccountChange} className="form-control" />
                                          </div>
                                          <div className="d-flex justify-content-end">
                                            <button className="btn btn-primary" onClick={handleSaveAccount} disabled={isSavingAccount}>
                                              {isSavingAccount ? 'Saving...' : 'Save Changes'}
                                            </button>
                                          </div>
                                          {passwordResetMessage && (
                                            <div className="mt-3"><span className="text-success">{passwordResetMessage}</span></div>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    <div className="col-md-12">
                                      <div className="card">
                                        <div className="card-body d-flex align-items-center justify-content-between">
                                          <div>
                                            <h6 className="mb-1">Delete User</h6>
                                            <div className="text-muted small">This action cannot be undone.</div>
                                          </div>
                                          <button className="btn btn-outline-danger" onClick={() => handleUserDelete(selectedUser)}>Delete User</button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateRole; 